import { loadFontPair } from './loadFontPreset'
import { getContrastRatio, readableTextOn, LIGHT_TEXT, deriveStatusColor, SUCCESS_HUE, ERROR_HUE } from './colorUtils'

/**
 * All CSS custom properties that applyTheme sets.
 * Used by clearRootTheme() to remove inline overrides from :root.
 */
const THEME_PROPERTIES = [
  '--color-bg', '--color-text',
  '--color-header', '--color-header-text',
  '--color-footer', '--color-footer-text',
  '--color-accent', '--color-accent-text', '--icon-filter-accent',
  '--color-success', '--color-error',
  '--color-highlight', '--color-highlight-text',
  '--color-outline-button', '--color-outline-button-text',
  '--font-decorative', '--font-primary'
]

/**
 * Apply a theme object to all .visitor-theme elements in the DOM.
 * Visitor-facing pages get .visitor-theme on #app; admin previews get it
 * on their preview containers. Admin UI inherits defaults from theme.css.
 *
 * @param {object} theme — artistConfig.theme (or localTheme from ThemeSettings)
 */
export function applyTheme(theme = {}) {
  const targets = document.querySelectorAll('.visitor-theme')
  if (!targets.length) return

  targets.forEach(el => {
    // Never theme #app when admin layout is active — prevents race
    // where theme loads before auth resolves and leaks into admin
    if (el.id === 'app' && el.classList.contains('layout-admin')) return
    applyThemeToElement(theme, el)
  })

  if (theme.fontHeading && theme.fontBody) {
    loadFontPair(theme.fontHeading, theme.fontBody)
  }
}

/**
 * Apply theme to :root before the app is visible — used as a pre-paint step
 * when cached config is available but .visitor-theme elements don't exist yet.
 * clearRootTheme() cleans this up when switching to admin/platform layout.
 */
export function prePaintTheme(theme = {}) {
  applyThemeToElement(theme, document.documentElement)
  if (theme.fontHeading && theme.fontBody) {
    loadFontPair(theme.fontHeading, theme.fontBody)
  }
}

/**
 * Remove all inline theme overrides from :root.
 * Called when switching from visitor to admin layout.
 */
export function clearRootTheme() {
  const targets = [document.documentElement, document.getElementById('app')]
  for (const el of targets) {
    if (!el) continue
    for (const prop of THEME_PROPERTIES) {
      el.style.removeProperty(prop)
    }
  }
}

/**
 * Set all theme CSS custom properties on a single element.
 */
function applyThemeToElement(theme, el) {
  // ── Base colors ──
  if (theme.bgColor)      el.style.setProperty('--color-bg',      theme.bgColor)
  if (theme.textColor)    el.style.setProperty('--color-text',    theme.textColor)
  if (theme.headerColor)  el.style.setProperty('--color-header',  theme.headerColor)
  if (theme.headerTextColor) el.style.setProperty('--color-header-text', theme.headerTextColor)
  const footerColor = theme.footerColor || theme.headerColor
  if (footerColor)        el.style.setProperty('--color-footer',  footerColor)
  if (theme.footerTextColor) el.style.setProperty('--color-footer-text', theme.footerTextColor)
  if (theme.accentColor) {
    el.style.setProperty('--color-accent', theme.accentColor)
    const accentText = readableTextOn(theme.accentColor)
    el.style.setProperty('--color-accent-text', accentText)
    el.style.setProperty('--icon-filter-accent', accentText === LIGHT_TEXT ? 'brightness(0) invert(1)' : 'none')
  }
  el.style.setProperty('--color-success', deriveStatusColor(SUCCESS_HUE, theme.accentColor))
  el.style.setProperty('--color-error', deriveStatusColor(ERROR_HUE, theme.accentColor))

  // ── Highlight color (ongoing events, emphasis badges) ──
  const highlightColor = theme.highlightColor || theme.accentColor
  if (highlightColor) {
    el.style.setProperty('--color-highlight', highlightColor)
    el.style.setProperty('--color-highlight-text', readableTextOn(highlightColor))
  }

  // ── Derived: outline button color ──
  if (theme.accentColor && theme.bgColor) {
    const accentOnBg = getContrastRatio(theme.accentColor, theme.bgColor)
    const usesAccent = accentOnBg >= 4.5
    const outlineColor = usesAccent ? theme.accentColor : (theme.textColor || '#212529')
    el.style.setProperty('--color-outline-button', outlineColor)
    const outlineFilledText = usesAccent
      ? readableTextOn(theme.accentColor)
      : (theme.bgColor || '#f9f9f8')
    el.style.setProperty('--color-outline-button-text', outlineFilledText)
  }
}
